import React, { useEffect, useState } from "react";
import AddContactButton from "./components/Modals/AddContactButton";
import { useAppServices } from "../../../../hook/services";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";

const PERMISSION_LABELS = {
  agency_admins: "Agency admins",
  agency_users: "Agency users",
  account_admins: "Account admins",
  account_users: "Account users",
};

const ContactButtons = () => {
  const AppService = useAppServices();
  const { companyId, type, customizationId } = useParams();
  const [contactButtons, setContactButtons] = useState([]);
  const [data, setData] = useState({});
  const [loader, setLoader] = useState(true);
  const [deletingIndex, setDeletingIndex] = useState(null);

  const handler =
    type === "agency" || type === "location"
      ? AppService.sidenav_customizer_main_level
      : type === "group"
      ? AppService.sidenav_customizer_custom_level
      : AppService.sidenav_customizer_saas_level;

  const query =
    type === "location" || type === "agency"
      ? `companyId=${companyId}&type=${type}`
      : `_id=${customizationId}`;

  const getData = async () => {
    setLoader(true);
    try {
      const { response } = await handler.filter({ query });
      if (response?.data) {
        setContactButtons(response.data.contact_buttons || []);
        setData(response.data);
      } else {
        setContactButtons([]);
        setData({});
      }
    } catch (error) {
      console.error("Failed to fetch contact buttons:", error);
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    getData();
  }, []);

  const handleDelete = async (editIndex) => {
    if (editIndex === null || editIndex < 0 || !data?._id) {
      toast.error("Invalid delete request.");
      return;
    }

    const contact_buttons = data?.contact_buttons
      ? [...data.contact_buttons]
      : [];
    contact_buttons.splice(editIndex, 1);

    const payload = {
      contact_buttons,
      companyId,
      _id: data._id,
    };

    try {
      setDeletingIndex(editIndex);
      const { response } = await handler.update({ payload });
      if (response) {
        toast.success("Button deleted.");
        getData();
      } else {
        toast.error("Failed to delete the button.");
      }
    } catch (error) {
      console.error("Delete error:", error);
      toast.error("An error occurred while deleting.");
    } finally {
      setDeletingIndex(null);
    }
  };

  const enabledRoles = (perms) => {
    if (!perms) return [];
    return Object.keys(perms).filter((k) => perms[k]);
  };

  return (
    <div className="cb-list-wrapper">
      <style>{`
        .cb-list-wrapper {
          background: #ffffff;
          border-radius: 16px;
          padding: 22px 24px;
          border: 1px solid #e2e8f0;
        }
        .cb-list-header {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 16px;
          flex-wrap: wrap;
          margin-bottom: 20px;
        }
        .cb-list-title {
          margin: 0;
          font-size: 18px;
          font-weight: 600;
          color: #0f172a;
          display: flex;
          align-items: center;
          gap: 10px;
        }
        .cb-list-title .cb-badge {
          background: #eef2ff;
          color: #4338ca;
          font-size: 12px;
          font-weight: 600;
          border-radius: 999px;
          padding: 2px 10px;
        }
        .cb-list-subtitle {
          color: #64748b;
          font-size: 13px;
          margin: 4px 0 0;
        }
        .cb-empty {
          border: 1px dashed #cbd5e1;
          border-radius: 14px;
          padding: 48px 20px;
          text-align: center;
          background: linear-gradient(180deg, #f8fafc 0%, #eef2ff 100%);
        }
        .cb-empty .cb-empty-icon {
          width: 56px;
          height: 56px;
          border-radius: 16px;
          background: #fff;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          color: #4f46e5;
          font-size: 26px;
          box-shadow: 0 8px 24px rgba(79, 70, 229, 0.18);
          margin-bottom: 14px;
        }
        .cb-empty h6 {
          font-size: 15px;
          color: #0f172a;
          font-weight: 600;
          margin: 0 0 4px;
        }
        .cb-empty p {
          color: #64748b;
          font-size: 13px;
          margin: 0 0 14px;
        }
        .cb-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
          gap: 14px;
        }
        .cb-card {
          background: #ffffff;
          border: 1px solid #e2e8f0;
          border-radius: 14px;
          padding: 16px;
          display: flex;
          flex-direction: column;
          gap: 12px;
          transition: border-color 0.15s, box-shadow 0.15s;
        }
        .cb-card:hover {
          border-color: #c7d2fe;
          box-shadow: 0 8px 24px rgba(15, 23, 42, 0.06);
        }
        .cb-card-preview {
          background: linear-gradient(180deg, #f8fafc 0%, #eef2ff 100%);
          border-radius: 10px;
          padding: 18px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .cb-card-btn {
          border: none;
          border-radius: 8px;
          padding: 8px 16px;
          font-weight: 500;
          font-size: 13px;
          color: #fff;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          box-shadow: 0 4px 12px rgba(15, 23, 42, 0.15);
          cursor: default;
        }
        .cb-card-meta {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }
        .cb-card-meta .cb-card-name {
          font-size: 14px;
          font-weight: 600;
          color: #0f172a;
          margin: 0;
          word-break: break-word;
        }
        .cb-card-meta .cb-card-row {
          font-size: 12px;
          color: #64748b;
          display: flex;
          align-items: center;
          gap: 6px;
          word-break: break-all;
        }
        .cb-card-meta .cb-card-row > span.text-truncate {
          min-width: 0;
          overflow: hidden;
          white-space: nowrap;
          text-overflow: ellipsis;
        }
        .cb-pill {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          font-size: 11px;
          padding: 2px 8px;
          border-radius: 999px;
          font-weight: 500;
          flex-shrink: 0;
        }
        .cb-pill.url { background: #ecfeff; color: #0e7490; }
        .cb-pill.contact { background: #f0fdf4; color: #15803d; }
        .cb-pill.fields { background: #fef3c7; color: #92400e; }
        .cb-pill.role { background: #eef2ff; color: #4338ca; font-size: 10px; padding: 1px 7px; }
        .cb-card-flags {
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
        }
        .cb-card-roles {
          display: flex;
          flex-wrap: wrap;
          gap: 4px;
          padding-top: 8px;
          border-top: 1px solid #f1f5f9;
        }
        .cb-card-roles .cb-roles-label {
          font-size: 11px;
          color: #94a3b8;
          font-weight: 500;
          margin-right: 2px;
          align-self: center;
        }
        .cb-card-actions {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-top: 4px;
          padding-top: 12px;
          border-top: 1px solid #f1f5f9;
        }
        .cb-card-actions .cb-action-btn {
          flex: 1;
          border: 1px solid #e2e8f0;
          background: #fff;
          color: #475569;
          border-radius: 8px;
          padding: 6px 10px;
          font-size: 12px;
          font-weight: 500;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          transition: all 0.15s;
        }
        .cb-card-actions .cb-action-btn:hover {
          background: #f8fafc;
          color: #0f172a;
        }
        .cb-card-actions .cb-action-btn.delete {
          color: #b91c1c;
          border-color: #fecaca;
          background: #fef2f2;
        }
        .cb-card-actions .cb-action-btn.delete:hover {
          background: #fee2e2;
          color: #991b1b;
        }
        .cb-card-actions .cb-edit-slot { flex: 1; display: flex; }
        .cb-card-actions .cb-edit-slot > * { width: 100%; }
        .cb-card-actions .cb-edit-slot .btn {
          width: 100%;
          padding: 6px 10px;
          font-size: 12px;
          font-weight: 500;
          gap: 6px;
        }
        .cb-skeleton {
          height: 220px;
          border-radius: 14px;
          background: linear-gradient(90deg, #f1f5f9 0%, #e2e8f0 50%, #f1f5f9 100%);
          background-size: 200% 100%;
          animation: cbSkeleton 1.5s ease-in-out infinite;
        }
        @keyframes cbSkeleton {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
      `}</style>

      <div className="cb-list-header">
        <div>
          <h3 className="cb-list-title">
            <i className="bi bi-person-lines-fill text-primary" />
            Contact Buttons
            {!loader && (
              <span className="cb-badge">{contactButtons.length}</span>
            )}
          </h3>
          <p className="cb-list-subtitle">
            Buttons that appear on every contact's detail page in GHL.
          </p>
        </div>
        {!loader && <AddContactButton data={data} handleRefresh={getData} />}
      </div>

      {loader ? (
        <div className="cb-grid">
          <div className="cb-skeleton" />
          <div className="cb-skeleton" />
          <div className="cb-skeleton" />
        </div>
      ) : contactButtons.length === 0 ? (
        <div className="cb-empty">
          <div className="cb-empty-icon">
            <i className="bi bi-person-lines-fill" />
          </div>
          <h6>No contact buttons yet</h6>
          <p>
            Add a button to give your team quick actions on every contact's
            page.
          </p>
          <AddContactButton data={data} handleRefresh={getData} />
        </div>
      ) : (
        <div className="cb-grid">
          {contactButtons.map((item, index) => {
            const roles = enabledRoles(item.user_permissions);
            return (
              <div className="cb-card" key={index}>
                <div className="cb-card-preview">
                  <span
                    className="cb-card-btn"
                    style={{ background: item.colorPicker || "#4f46e5" }}
                    title={item.tooltip || item.text}
                  >
                    {item.icon && (
                      <i
                        className={`${item.iconFamily || "fas"} fa-${item.icon}`}
                        style={{ fontSize: 13 }}
                      />
                    )}
                    <span>{item.text}</span>
                  </span>
                </div>

                <div className="cb-card-meta">
                  <p className="cb-card-name" title={item.text}>
                    {item.text}
                  </p>
                  {item.linkOrCustomValue && (
                    <div className="cb-card-row">
                      <span className="cb-pill url">
                        <i className="bi bi-link-45deg" />
                        URL
                      </span>
                      <span
                        className="text-truncate"
                        title={item.linkOrCustomValue}
                      >
                        {item.linkOrCustomValue}
                      </span>
                    </div>
                  )}
                  <div className="cb-card-flags">
                    {item.sendContact && (
                      <span className="cb-pill contact">
                        <i className="bi bi-person-check" />
                        Contact data
                      </span>
                    )}
                    {item.customFields && (
                      <span className="cb-pill fields">
                        <i className="bi bi-list-task" />
                        Custom fields
                      </span>
                    )}
                  </div>
                </div>

                {roles.length > 0 && (
                  <div className="cb-card-roles">
                    <span className="cb-roles-label">Visible to:</span>
                    {roles.map((r) => (
                      <span key={r} className="cb-pill role">
                        {PERMISSION_LABELS[r] || r}
                      </span>
                    ))}
                  </div>
                )}

                <div className="cb-card-actions">
                  <div className="cb-edit-slot">
                    <AddContactButton
                      data={data}
                      handleRefresh={getData}
                      selectedButton={item}
                      editIndex={index}
                    />
                  </div>
                  <button
                    className="cb-action-btn delete"
                    title="Delete"
                    onClick={() => handleDelete(index)}
                    disabled={deletingIndex === index}
                  >
                    {deletingIndex === index ? (
                      <span
                        className="spinner-border spinner-border-sm"
                        role="status"
                        aria-hidden="true"
                      />
                    ) : (
                      <>
                        <i className="bi bi-trash" />
                        Delete
                      </>
                    )}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ContactButtons;
